import httpClient from '../../lib/httpClient';
import { ENDPOINTS } from './endpoints';
import { useSidebarVisibilityStore } from '../../store/sidebarVisibilityStore';

export const billingApi = {
  // Creates a Stripe SetupIntent on the firm's Stripe Customer. Returns
  // { clientSecret, publishableKey, stripeCustomerId } that the browser hands to Stripe.js
  // to attach a card or bank account in a single confirmCardSetup / confirmUsBankAccountSetup
  // call. May return 409 STRIPE_CUSTOMER_NOT_PROVISIONED if HaloPSA hasn't populated
  // stripe_customer_id yet — the caller must surface that to the user.
  createSetupIntent: async (payload) => {
    const response = await httpClient.post(ENDPOINTS.BILLING.SETUP_INTENT, payload);
    return response.data;
  },

  // The active site's tax inputs: the customer's HaloPSA tax (wins when set), the portal's state
  // tax table, and the site's saved state — applied by resolveTax (shared/pricing/pricingCalculator).
  getTaxContext: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.TAX_CONTEXT);
    return response.data?.data ?? null;
  },

  getPaymentMethods: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.PAYMENT_METHODS);
    return response.data;
  },

  savePaymentMethod: async (payload) => {
    const response = await httpClient.post(ENDPOINTS.BILLING.PAYMENT_METHODS, payload);
    return response.data;
  },

  deletePaymentMethod: async (token) => {
    const response = await httpClient.delete(ENDPOINTS.BILLING.DELETE_METHOD(token));
    return response.data;
  },

  setDefaultPaymentMethod: async (token) => {
    const response = await httpClient.put(ENDPOINTS.BILLING.SET_DEFAULT(token));
    return response.data;
  },

  verifyAch: async (payload) => {
    const response = await httpClient.post(ENDPOINTS.BILLING.VERIFY_ACH, payload);
    return response.data;
  },

  // NACHA debit-authorization mandate. Returns { version, text, hash }. The dialog
  // must render `text` verbatim and echo `hash` back inside savePaymentMethod's
  // `achMandate` field — the server rejects the save if the hash doesn't match the
  // current text on file.
  getAchMandate: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.ACH_MANDATE);
    return response.data;
  },

  getInvoices: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.GET_INVOICES);
    return response.data;
  },

  // ────────── Auto-renewal (Phase 2) ──────────

  // Returns { success, data: { enabled, paymentMethod: { label, lastFour, brand, bankName } | null } }
  // May surface 409 STRIPE_CUSTOMER_REBOUND if the HaloPSA stripe_customer_id changed since
  // the preference was last set — UI should treat that as "auto-renewal force-disabled" and
  // ask the user to re-confirm.
  getAutoRenewal: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.AUTO_RENEWAL);
    return response.data;
  },

  // payload: { enabled: bool, paymentMethodToken: string | null }
  // When enabling, the selected method is synced to HaloPSA via POST /api/Client so HaloPSA's
  // native Stripe integration auto-charges future recurring invoices.
  setAutoRenewal: async (payload) => {
    const response = await httpClient.put(ENDPOINTS.BILLING.AUTO_RENEWAL, payload);
    return response.data;
  },

  // Returns the recommended payment method to default to when the toggle is first turned ON.
  // { success, data: { suggestion: SavedPaymentMethodDto | null, reason: 'stripe-default' | 'newest' | 'none' } }
  getAutoRenewalSuggestion: async () => {
    const response = await httpClient.get(ENDPOINTS.BILLING.AUTO_RENEWAL_SUGGESTION);
    return response.data;
  },

  // POST /api/billing/invoices/{id}/pay — Phase 4. Real Stripe charging:
  //   200 { status: 'paid', paymentIntentId } — card success, HaloPSA marked paid
  //   202 { status: 'processing', paymentIntentId } — ACH submitted, webhook will finalize
  //   202 { status: 'partial', paymentIntentId } — charge succeeded but HaloPSA mark-paid failed
  //   402 { errorCode, message } — payment declined
  // X-Idempotency-Key is required (CheckoutIdempotencyFilter on the controller).
  payInvoice: async (invoiceId, payload, { idempotencyKey } = {}) => {
    const response = await httpClient.post(
      ENDPOINTS.BILLING.PAY_INVOICE(invoiceId),
      payload,
      { headers: idempotencyKey ? { 'X-Idempotency-Key': idempotencyKey } : {} },
    );
    // Invoice payment can move the lifecycle stage (4a → 4b) — refetch sidebar visibility
    useSidebarVisibilityStore.getState().refresh();
    return response.data;
  },

  // POST /api/billing/invoices/{id}/void — flags the invoice voided in HaloPSA and appends the
  // reason to its notes, for a customer who chose "Discard Invoice" instead of paying a
  // freshly-created mid-cycle invoice. Server-side gates (own firm, fully unpaid, Payment Option
  // = Pay Immediately) mean this can legitimately 404/409, so callers should treat a failure as
  // non-blocking and still navigate away — leaving the invoice to the existing manual
  // reconciliation rather than trapping the user on the page.
  voidInvoice: async (invoiceId) => {
    const response = await httpClient.post(ENDPOINTS.BILLING.VOID_INVOICE(invoiceId));
    // Voiding clears the outstanding balance that gates parts of the app (Manage Licenses,
    // Downloads, Add Product), so the sidebar lifecycle answer can change — same as paying.
    useSidebarVisibilityStore.getState().refresh();
    return response.data;
  },
};
